import { alpha } from '@mui/material/styles';
import { tss } from 'tss-react/mui';

export const useStyles = tss.create(({ theme }) => ({
  main: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  searchField: {
    flex: '1 1 240px',
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(1),
      backgroundColor:
        theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.background.paper,
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
  countLabel: {
    flexShrink: 0,
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
  },
  title: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  addButton: {
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    fontWeight: 500,
  },
  profileCard: {
    height: '100%',
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.background.paper,
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    transition: 'box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out, transform 0.2s ease',
    '&:hover': {
      boxShadow:
        theme.palette.mode === 'dark'
          ? `0 8px 24px ${alpha(theme.palette.common.black, 0.4)}`
          : `0 8px 24px ${alpha(theme.palette.common.black, 0.06)}`,
      borderColor:
        theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.light,
      transform: 'translateY(-2px)',
    },
  },
  profileCardInner: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: theme.spacing(2.5),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  profileCardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    minWidth: 0,
  },
  profileAvatar: {
    flexShrink: 0,
    width: 44,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.grey[200],
    color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : theme.palette.grey[500],
  },
  profileName: {
    flex: 1,
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  profileMeta: {
    marginTop: theme.spacing(1.25),
  },
  openingLineChip: {
    height: 22,
    fontSize: '0.75rem',
    borderColor: theme.palette.divider,
    color: theme.palette.text.secondary,
  },
  profilePrompt: {
    flex: 1,
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: 1.55,
    maxHeight: '4.65em',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
  },
  profilePromptEmpty: {
    flex: 1,
    color: theme.palette.text.disabled,
    fontSize: '0.8125rem',
    fontStyle: 'italic',
    lineHeight: 1.55,
  },
  profileCardDivider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  profileCardActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginTop: 'auto',
  },
  actionGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    padding: theme.spacing(0.25),
    borderRadius: theme.spacing(1),
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[100],
    border: `1px solid ${theme.palette.divider}`,
  },
  actionButton: {
    minWidth: 'auto',
    width: 32,
    height: 32,
    padding: 0,
    borderRadius: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
    '&.Mui-disabled': {
      opacity: 0.38,
    },
  },
  actionButtonDelete: {
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.error.main, 0.12)
          : theme.palette.error.light,
      color: theme.palette.error.main,
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  emptyStateIcon: {
    fontSize: '4rem',
    marginBottom: theme.spacing(2),
    opacity: 0.5,
  },
  modalContent: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(2),
  },
  modalForm: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
  },
  modalPaper: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
  },
  filledField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor:
        theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[100],
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.divider,
      },
    },
  },
  readOnlyField: {
    '& .MuiInputBase-input': {
      cursor: 'default',
      color: theme.palette.text.primary,
      WebkitTextFillColor: theme.palette.text.primary,
    },
  },
  promptBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
    maxHeight: 'min(360px, 40vh)',
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[100],
  },
  promptBoxRaw: {
    fontSize: '0.8125rem',
    lineHeight: 1.7,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    color: theme.palette.text.primary,
    padding: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  modalActions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  agent_profile_template_select: {
    ['.MuiSelect-select']: {
      width: '300px',
    },
    [`> svg`]: {
      marginRight: '4px',
    },
  },
}));
